/**
 * Design tokens for the Legacy Ball Table Management interface.
 *
 * Design system:
 * - Serif/semi-serif headings (Georgia, 'Playfair Display', serif)
 * - Muted neutral palette with primary accent (#1a6b3c)
 * - Minimum 4.5:1 contrast ratio for all text
 * - Zone color indicators on all table representations
 */

/** Primary accent color (KDU green) */
export const PRIMARY = '#1a6b3c';

/** Dark text color — used for headings and primary content (contrast 12.6:1 on white) */
export const TEXT_DARK = '#1a2e23';

/** Body text color (contrast 7.5:1 on white) */
export const TEXT_BODY = '#2d3b34';

/** Secondary text color (contrast 4.8:1 on white) */
export const TEXT_SECONDARY = '#4a5d52';

/** Muted text for labels (contrast 4.6:1 on white) */
export const TEXT_MUTED = '#5c6b63';

/** Heading font stack — serif/semi-serif for elegance */
export const FONT_HEADING = "Georgia, 'Playfair Display', 'Times New Roman', serif";

/** Body font stack — inherits from app */
export const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/** Background colors */
export const BG_PAGE = '#fafbfa';
export const BG_CARD = '#ffffff';
export const BG_SURFACE = '#f5f7f6';
export const BG_MUTED = '#eef1ef';

/** Border colors */
export const BORDER_LIGHT = '#e2e8e4';
export const BORDER_DEFAULT = '#d0d8d3';

/** Zone colors — distinct, accessible on white backgrounds */
export const ZONE_COLORS: Record<string, string> = {
  VIP: '#8b1a2b',
  Legacy: '#1a6b3c',
  Sponsor: '#b8860b',
  Chapter: '#1565c0',
  Family: '#6a1b9a',
  Overflow: '#546e7a',
  Accessibility: '#00695c',
  Media: '#e65100',
};

/** Status colors for table/seat statuses */
export const STATUS_COLORS: Record<string, string> = {
  Available: '#2e7d32',
  PartiallyReserved: '#e65100',
  FullyReserved: '#c62828',
  Held: '#7b1fa2',
  Blocked: '#37474f',
  Assigned: '#1565c0',
  CheckedIn: '#6a1b9a',
};

/** Status background colors (light tints) */
export const STATUS_BG: Record<string, string> = {
  Available: '#e8f5e9',
  Assigned: '#e3f2fd',
  Held: '#f3e5f5',
  Blocked: '#eceff1',
  CheckedIn: '#ede7f6',
  PartiallyReserved: '#fff3e0',
  FullyReserved: '#ffebee',
};

/** Shared heading style for section titles */
export const headingStyle = {
  fontFamily: FONT_HEADING,
  fontWeight: 700,
  color: TEXT_DARK,
  margin: 0,
} as const;

/** Shared sub-heading style */
export const subHeadingStyle = {
  fontFamily: FONT_HEADING,
  fontWeight: 600,
  color: TEXT_DARK,
  margin: 0,
} as const;
